/**
 * PriceLab - Pricing Calculator App
 * Multi-marketplace pricing tool with unified marketplace management
 */

import { useState, useEffect } from 'react';
import { Layout } from './components/common';
import type { ModuleType } from './components/common/Layout';
import { CustomMarketplacePage } from './components/marketplace';
import { PriceComparisonPage } from './components/priceComparison';
import { ShippingPage } from './components/shipping';
import { SettingsPage } from './components/settings';
import { ProfitabilityPage } from './components/profitability';
import { Package, Truck, Database, Link2, Store } from 'lucide-react';
import { useLanguage } from './i18n';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import {
  getDatabaseStats,
  initializeDefaultMarketplaces,
  loadAutoBackup,
  saveAutoBackup,
} from './utils/database';
import type { DatabaseStats } from './types/database';
import { logger } from './utils/logger';

function AppContent() {
  const { t, language } = useLanguage();
  const { user: _user, isLoading: authLoading } = useAuth();
  const [activeModule, setActiveModule] = useState<ModuleType>('dashboard');
  const [dbStats, setDbStats] = useState<DatabaseStats | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize: Try to load from localStorage backup first, then initialize defaults
  useEffect(() => {
    const init = async () => {
      try {
        // First, try to restore from localStorage backup
        const restored = await loadAutoBackup();
        if (restored) {
          logger.debug('Data restored from auto-backup');
        }

        // Initialize default marketplaces if none exist, or update countries
        await initializeDefaultMarketplaces(true);

        // Load database stats
        const stats = await getDatabaseStats();
        setDbStats(stats);
        setIsInitialized(true);
      } catch (error) {
        logger.error('Initialization error:', error);
        setIsInitialized(true);
      }
    };
    init();
  }, []);

  // Auto-save to localStorage whenever stats change (indicating data change)
  useEffect(() => {
    if (isInitialized && dbStats) {
      // Debounce auto-save
      const timer = setTimeout(async () => {
        await saveAutoBackup();
      }, 2000); // 2 second debounce

      return () => clearTimeout(timer);
    }
  }, [isInitialized, dbStats]);

  // Reload stats and trigger auto-save
  const reloadStats = async () => {
    try {
      const stats = await getDatabaseStats();
      setDbStats(stats);
      // Trigger auto-save after data change
      await saveAutoBackup();
    } catch (error) {
      logger.error('Failed to reload stats:', error);
    }
  };

  const renderModule = () => {
    switch (activeModule) {
      case 'marketplaces':
        return <CustomMarketplacePage />;

      case 'priceComparison':
        return <PriceComparisonPage />;

      case 'shipping':
        return <ShippingPage />;

      case 'profitability':
        return <ProfitabilityPage />;

      case 'settings':
        return <SettingsPage onStatsChange={reloadStats} />;

      case 'dashboard':
      default:
        return (
          <div className="space-y-6">
            {/* Welcome Card */}
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-8 text-white">
              <h2 className="text-2xl font-bold mb-2">{t.welcome.title}</h2>
              <p className="opacity-90">{t.welcome.description}</p>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Database className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-sm font-medium text-slate-600">{t.stats.products}</span>
                </div>
                <div className="text-2xl font-bold text-slate-800">{dbStats?.totalProducts || 0}</div>
                <div className="text-xs text-slate-500">{t.stats.inDatabase}</div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Package className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="text-sm font-medium text-slate-600">{t.stats.categories}</span>
                </div>
                <div className="text-2xl font-bold text-slate-800">{dbStats?.categoriesCount || 0}</div>
                <div className="text-xs text-slate-500">{t.stats.productCategories}</div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Store className="w-5 h-5 text-orange-600" />
                  </div>
                  <span className="text-sm font-medium text-slate-600">{t.stats.skus}</span>
                </div>
                <div className="text-2xl font-bold text-slate-800">{dbStats?.totalSKUs || 0}</div>
                <div className="text-xs text-slate-500">{t.stats.mappedToMarketplaces}</div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Link2 className="w-5 h-5 text-purple-600" />
                  </div>
                  <span className="text-sm font-medium text-slate-600">{t.stats.lastImport}</span>
                </div>
                <div className="text-sm font-bold text-slate-800">
                  {dbStats?.lastImport
                    ? new Date(dbStats.lastImport).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US')
                    : t.stats.never}
                </div>
                <div className="text-xs text-slate-500">{t.stats.dataRefresh}</div>
              </div>
            </div>

            {/* Modules */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">{t.modules.title}</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <button
                  onClick={() => setActiveModule('marketplaces')}
                  className="flex items-center gap-3 p-4 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors text-left"
                >
                  <Store className="w-8 h-8 text-indigo-600" />
                  <div>
                    <div className="font-medium text-slate-800">{t.modules.marketplaces}</div>
                    <div className="text-sm text-slate-500">{t.modules.marketplacesDesc}</div>
                  </div>
                </button>
                <button
                  onClick={() => setActiveModule('shipping')}
                  className="flex items-center gap-3 p-4 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors text-left"
                >
                  <Truck className="w-8 h-8 text-amber-600" />
                  <div>
                    <div className="font-medium text-slate-800">{t.modules.shipping}</div>
                    <div className="text-sm text-slate-500">{t.modules.shippingDesc}</div>
                  </div>
                </button>
                <button
                  onClick={() => setActiveModule('settings')}
                  className="flex items-center gap-3 p-4 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors text-left"
                >
                  <Database className="w-8 h-8 text-slate-600" />
                  <div>
                    <div className="font-medium text-slate-800">{language === 'tr' ? 'Ayarlar' : 'Settings'}</div>
                    <div className="text-sm text-slate-500">{language === 'tr' ? 'Veri yönetimi ve kurlar' : 'Data management & rates'}</div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        );
    }
  };

  if (authLoading || !isInitialized) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center" role="status" aria-live="polite">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4" aria-hidden="true" />
          <p className="text-slate-600">{authLoading ? 'Authenticating...' : t.common.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <Layout activeModule={activeModule} onModuleChange={setActiveModule}>
      {renderModule()}
    </Layout>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
